import { Button } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { shopsApi } from 'api';
import {
  DataWrapper,
  FormActions,
  FormControls,
  FormikCheckbox,
  FormikTextField,
} from 'components';
import { QueryKey, ShopIntegrationType } from 'enums';
import { useMutation } from 'hooks';
import { ShopIntegrationConfig } from 'types';

type Props = {
  shopId: string;
  navigateBack: () => void;
};

type Values = ShopIntegrationConfig;

export const ShopIntegrations: React.FC<Props> = ({ shopId, navigateBack }) => {
  const { t: tCommon } = useTranslation();

  const queryClient = useQueryClient();

  const [initialValues, setInitialValues] = useState<Values>({
    enabled: false,
    type: ShopIntegrationType.RosaOne,
    publicKey: '',
    privateKey: '',
  });

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object().shape({
        enabled: Yup.boolean().required(tCommon('errors.required')),
        type: Yup.string()
          .required(tCommon('errors.required'))
          .oneOf([ShopIntegrationType.RosaOne]),
        publicKey: Yup.string().when('enabled', {
          is: true,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
        privateKey: Yup.string().when('enabled', {
          is: true,
          then: (schema) => schema.required(tCommon('errors.required')),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [tCommon],
  );

  const integrationType = useMemo(
    () => tCommon('shop_integration_config.types.rosa_one'),
    [tCommon],
  );

  const queryResult = useQuery(
    QueryKey.ShopIntegrations,
    shopsApi.getIntegrationConfig(shopId),
    {
      onSuccess: (data) => {
        if (data) {
          setInitialValues({
            enabled: data.enabled,
            type: data.type,
            publicKey: data.publicKey || '',
            privateKey: data.privateKey || '',
          });
        }
      },
    },
  );
  const { mutate: updateShopIntegrations } = useMutation<
    ShopIntegrationConfig,
    AxiosError,
    {
      shopId: string;
      data: ShopIntegrationConfig;
    },
    unknown
  >(shopsApi.updateIntegrationConfig);

  const handleSubmit = useCallback(
    (values: Values, helpers: FormikHelpers<Values>) => {
      updateShopIntegrations(
        { shopId, data: values },
        {
          onSuccess: () =>
            queryClient.invalidateQueries(QueryKey.ShopIntegrations),
          onSettled: () => helpers.setSubmitting(false),
        },
      );
    },
    [shopId, queryClient, updateShopIntegrations],
  );

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <DataWrapper queryResult={queryResult} ignoreState={{ empty: true }}>
          <Form className="tw-max-w-lg">
            <FormControls>
              <div className="tw-flex">
                <FormikCheckbox label={integrationType} name="enabled" />
              </div>

              {formik.values.enabled && (
                <Fragment>
                  <FormikTextField
                    label={tCommon('shop_integration_config.fields.public_key')}
                    name="publicKey"
                    required
                  />
                  <FormikTextField
                    label={tCommon(
                      'shop_integration_config.fields.private_key',
                    )}
                    name="privateKey"
                    required
                  />
                </Fragment>
              )}
            </FormControls>
            <FormActions>
              <Button variant="outlined" onClick={navigateBack}>
                {tCommon('buttons.cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={formik.isSubmitting || !formik.isValid}
                onClick={formik.submitForm}
              >
                {tCommon('buttons.save')}
              </Button>
            </FormActions>
          </Form>
        </DataWrapper>
      )}
    </Formik>
  );
};
